import { useState, useEffect } from 'react'
import { getTrainingBudget } from '@/lib/services/budgets.service'
import type { TrainingBudget } from '@/types/budgets'
import { formatCurrency, formatPercent } from '@/types/budgets'

interface TrainingHistoricalTableProps {
  currentYear: number
}

/**
 * Training Historical Table Component
 *
 * Displays training budget history for the last 3 years.
 * Shows budget, actual cost, variance, and utilization percentage for each year.
 *
 * Features:
 * - Fetches data for last 3 years (current year - 1, -2, -3)
 * - Table with 5 columns: Year, Budget, Actual, Variance, Utilization%
 * - Color-coded variance (green for under budget, red for over budget)
 * - Loading skeleton while fetching
 * - Empty state when no historical data available
 *
 * @param currentYear - Current budget year (excludes from historical view)
 */
export function TrainingHistoricalTable({ currentYear }: TrainingHistoricalTableProps) {
  const [historicalData, setHistoricalData] = useState<TrainingBudget[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchHistoricalData = async () => {
      setIsLoading(true)

      // Fetch data for last 3 years (currentYear - 1, -2, -3)
      const years = [currentYear - 1, currentYear - 2, currentYear - 3]
      const results: TrainingBudget[] = []

      // Fetch all years in parallel
      await Promise.all(
        years.map(async (year) => {
          try {
            const data = await getTrainingBudget(year)
            // Only include if has actual data (budget or actualCost > 0)
            if (data.budget > 0 || data.actualCost > 0) {
              results.push(data)
            }
          } catch (error) {
            // Ignore 404 errors (year not found) - this is expected
            if (
              !(error instanceof Error && error.message.includes('404')) &&
              !(error instanceof Error && error.message.includes('not found'))
            ) {
              console.error(`Error fetching training budget for ${year}:`, error)
            }
          }
        })
      )

      // Sort by year descending (most recent first)
      results.sort((a, b) => b.year - a.year)

      setHistoricalData(results)
      setIsLoading(false)
    }

    fetchHistoricalData()
  }, [currentYear])

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-xl font-semibold text-gray-900">Historical Data</h2>
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-full rounded bg-gray-200"></div>
          <div className="h-8 w-full rounded bg-gray-200"></div>
          <div className="h-8 w-full rounded bg-gray-200"></div>
        </div>
      </div>
    )
  }

  // Empty state
  if (historicalData.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-xl font-semibold text-gray-900">Historical Data</h2>
        <div className="rounded-md bg-gray-50 p-8 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p className="mt-4 text-sm text-gray-600">No historical data available</p>
          <p className="mt-1 text-xs text-gray-500">
            Training budget data from previous years will appear here
          </p>
        </div>
      </div>
    )
  }

  // Table with data
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <h2 className="mb-4 text-xl font-semibold text-gray-900">Historical Data</h2>
      <div className="overflow-hidden rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
              >
                Year
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500"
              >
                Budget
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500"
              >
                Actual
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500"
              >
                Variance
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500"
              >
                Utilization
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {historicalData.map((row) => (
              <tr key={row.year} className="hover:bg-gray-50">
                <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                  {row.year}
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-right text-sm text-gray-900">
                  {formatCurrency(row.budget)}
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-right text-sm text-gray-900">
                  {formatCurrency(row.actualCost)}
                </td>
                <td
                  className={`whitespace-nowrap px-6 py-4 text-right text-sm font-semibold ${
                    row.variance < 0
                      ? 'text-green-600'
                      : row.variance > 0
                        ? 'text-red-600'
                        : 'text-gray-900'
                  }`}
                >
                  {formatCurrency(row.variance)}
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-right text-sm text-gray-900">
                  {formatPercent(row.utilizationPercent, 1)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-4 text-xs text-gray-500">
        Showing training budget history for the last {historicalData.length} year(s)
      </p>
    </div>
  )
}
